import React, { useEffect, useState } from 'react';
import { useLandlordStore, MaintenanceRequest } from '@/stores/useLandlordStore';
import { 
    ArrowLeft, 
    User, 
    Building, 
    Calendar, 
    Clock, 
    FileText, 
    Image as ImageIcon,
    Phone,
    MessageSquare,
    CheckCircle,
    XCircle,
    AlertCircle,
    Loader2
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useToast } from '@/contexts/ToastContext';

interface MaintenanceDetail extends Omit<MaintenanceRequest, 'priority'> {
    tenant_email?: string;
    tenant_phone?: string;
    tenant_avatar?: string;
    priority?: 'Low' | 'Medium' | 'High' | 'Emergency';
    assigned_to?: string;
    estimated_cost?: number;
    completed_at?: string;
}

export const MaintenanceDetails: React.FC = () => {
    const { requestId } = useParams<{ requestId: string }>();
    const navigate = useNavigate();
    const { warning } = useToast();
    const [maintenance, setMaintenance] = useState<MaintenanceDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [showStatusDropdown, setShowStatusDropdown] = useState(false);

    // Mock fetch - in real app, this would be an API call
    useEffect(() => {
        const fetchMaintenanceDetails = async () => {
            setLoading(true);
            try {
                // Simulate API call
                await new Promise(resolve => setTimeout(resolve, 500));
                
                // Mock data - replace with actual API call
                const mockData: MaintenanceDetail = {
                    id: requestId || '',
                    title: 'Plumbing Issue',
                    description: 'The kitchen sink is clogged and water is not draining properly. This has been an issue for the past 3 days.',
                    priority: 'High',
                    status: 'Pending',
                    tenant_name: 'John Kamau',
                    tenant_email: 'john.kamau@example.com',
                    tenant_phone: '+254 712 345 678',
                    tenant_avatar: `https://ui-avatars.com/api/?name=John+Kamau&background=random`,
                    unit_number: 'A-101',
                    property_name: 'Sunrise Apartments',
                    property_id: 'prop_123',
                    images: [
                        'https://images.unsplash.com/photo-1584622050111-993a426fbf0a?w=400',
                        'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400'
                    ],
                    created_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
                    assigned_to: undefined,
                    estimated_cost: undefined
                };
                
                setMaintenance(mockData);
            } catch (error) {
                warning('Error', 'Failed to load maintenance details');
            } finally {
                setLoading(false);
            }
        };

        if (requestId) {
            fetchMaintenanceDetails();
        }
    }, [requestId, warning]);

    const handleUpdateStatus = async (newStatus: string) => {
        if (!maintenance) return;
        
        setActionLoading(true);
        setShowStatusDropdown(false);
        
        try {
            // Simulate API call
            await new Promise(resolve => setTimeout(resolve, 800));
            setMaintenance({ ...maintenance, status: newStatus as any });
        } catch (error) {
            warning('Error', 'Failed to update status');
        } finally {
            setActionLoading(false);
        }
    };

    const handleCall = () => {
        if (maintenance?.tenant_phone) {
            window.location.href = `tel:${maintenance.tenant_phone}`;
        }
    };

    const handleMessage = () => {
        if (maintenance?.tenant_email) {
            window.location.href = `mailto:${maintenance.tenant_email}`;
        }
    };

    const getStatusStyle = (status: string) => {
        switch (status) {
            case "Completed":
                return { text: "text-green-600 dark:text-green-400", bg: "bg-green-100 dark:bg-green-900/30", border: "border-green-500" };
            case "In Progress":
                return { text: "text-blue-600 dark:text-blue-400", bg: "bg-blue-100 dark:bg-blue-900/30", border: "border-blue-500" };
            case "Cancelled":
                return { text: "text-red-600 dark:text-red-400", bg: "bg-red-100 dark:bg-red-900/30", border: "border-red-500" };
            case "Pending":
            default:
                return { text: "text-yellow-600 dark:text-yellow-400", bg: "bg-yellow-100 dark:bg-yellow-900/30", border: "border-yellow-500" };
        }
    };

    const getPriorityStyle = (priority: string) => {
        switch (priority) {
            case "Emergency":
                return { text: "text-red-600 dark:text-red-400", bg: "bg-red-100 dark:bg-red-900/30" };
            case "High":
                return { text: "text-orange-600 dark:text-orange-400", bg: "bg-orange-100 dark:bg-orange-900/30" };
            case "Medium":
                return { text: "text-blue-600 dark:text-blue-400", bg: "bg-blue-100 dark:bg-blue-900/30" };
            case "Low":
                return { text: "text-gray-600 dark:text-gray-400", bg: "bg-gray-100 dark:bg-gray-900/30" };
            default:
                return { text: "text-gray-600 dark:text-gray-400", bg: "bg-gray-100 dark:bg-gray-900/30" };
        }
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh]">
                <Loader2 className="animate-spin text-primary w-10 h-10" />
                <p className="mt-4 text-light-subtle dark:text-dark-subtle">Loading maintenance details...</p>
            </div>
        );
    }

    if (!maintenance) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
                <AlertCircle className="w-16 h-16 text-red-500 mb-4" />
                <h2 className="text-xl font-bold text-light-text dark:text-dark-text">Maintenance Request Not Found</h2>
                <p className="mt-2 text-light-subtle dark:text-dark-subtle">The requested maintenance record could not be found.</p>
                <button
                    onClick={() => navigate(-1)}
                    className="mt-6 px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                >
                    Go Back
                </button>
            </div>
        );
    }

    const statusStyle = getStatusStyle(maintenance.status);
    const priorityStyle = getPriorityStyle(maintenance.priority || 'Medium');

    return (
        <div className="space-y-6 pb-12 min-h-[80vh]">
            {/* Header */}
            <div className="flex items-center gap-4 bg-light-card dark:bg-dark-card p-4 border-b border-light-border dark:border-dark-border">
                <button
                    onClick={() => navigate(-1)}
                    className="p-2 rounded-full hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
                >
                    <ArrowLeft size={24} className="text-primary dark:text-golden" />
                </button>
                <div>
                    <h1 className="text-xl font-bold text-light-text dark:text-dark-text">Maintenance Request Details</h1>
                    <p className="text-sm text-light-subtle dark:text-dark-subtle">Request #{maintenance.id.substring(0, 8)}</p>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 px-4">
                {/* Main Content */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Requester Info Card */}
                    <div className="bg-light-card dark:bg-dark-card rounded-xl p-6 border border-light-border dark:border-dark-border">
                        <h3 className="text-lg font-bold text-light-text dark:text-dark-text mb-4 flex items-center gap-2">
                            <User size={20} className="text-primary dark:text-golden" />
                            Request From
                        </h3>
                        <div className="flex items-center gap-4">
                            <img
                                src={maintenance.tenant_avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(maintenance.tenant_name)}&background=random`}
                                alt={maintenance.tenant_name}
                                className="w-16 h-16 rounded-full"
                            />
                            <div className="flex-1">
                                <h4 className="text-lg font-bold text-light-text dark:text-dark-text">{maintenance.tenant_name}</h4>
                                <p className="text-light-subtle dark:text-dark-subtle">
                                    {maintenance.property_name} - Unit {maintenance.unit_number}
                                </p>
                                <div className="flex gap-2 mt-3">
                                    <button
                                        onClick={handleCall}
                                        className="flex items-center gap-1 px-3 py-1.5 bg-light-bg dark:bg-dark-bg rounded-lg text-sm font-medium text-primary dark:text-golden hover:bg-light-bg/80 dark:hover:bg-dark-bg/80 transition-colors"
                                    >
                                        <Phone size={14} />
                                        Call
                                    </button>
                                    <button
                                        onClick={handleMessage}
                                        className="flex items-center gap-1 px-3 py-1.5 bg-light-bg dark:bg-dark-bg rounded-lg text-sm font-medium text-primary dark:text-golden hover:bg-light-bg/80 dark:hover:bg-dark-bg/80 transition-colors"
                                    >
                                        <MessageSquare size={14} />
                                        Message
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Request Details Card */}
                    <div className="bg-light-card dark:bg-dark-card rounded-xl p-6 border border-light-border dark:border-dark-border">
                        <h3 className="text-lg font-bold text-light-text dark:text-dark-text mb-4 flex items-center gap-2">
                            <FileText size={20} className="text-primary dark:text-golden" />
                            Request Details
                        </h3>
                        <div className="space-y-4">
                            <div>
                                <label className="text-sm text-light-subtle dark:text-dark-subtle">Category</label>
                                <p className="text-base font-semibold text-light-text dark:text-dark-text mt-1">{maintenance.title}</p>
                            </div>
                            <div>
                                <label className="text-sm text-light-subtle dark:text-dark-subtle">Description</label>
                                <p className="text-base text-light-text dark:text-dark-text mt-1">{maintenance.description}</p>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-sm text-light-subtle dark:text-dark-subtle">Priority</label>
                                    <span className={`inline-block mt-1 px-3 py-1 rounded-full text-sm font-medium ${priorityStyle.bg} ${priorityStyle.text}`}>
                                        {maintenance.priority || 'Medium'}
                                    </span>
                                </div>
                                <div>
                                    <label className="text-sm text-light-subtle dark:text-dark-subtle">Status</label>
                                    <div className="relative">
                                        <button
                                            onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                                            className={`mt-1 px-3 py-1 rounded-full text-sm font-medium ${statusStyle.bg} ${statusStyle.text} flex items-center gap-1`}
                                        >
                                            {maintenance.status}
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </button>
                                        {showStatusDropdown && (
                                            <div className="absolute left-0 mt-2 w-40 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-light-border dark:border-dark-border z-10">
                                                {['Pending', 'In Progress', 'Completed'].map(status => (
                                                    <button
                                                        key={status}
                                                        onClick={() => handleUpdateStatus(status)}
                                                        className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors first:rounded-t-lg last:rounded-b-lg"
                                                    >
                                                        {status}
                                                    </button>
                                                ))}
                                                <button
                                                    onClick={() => handleUpdateStatus('Cancelled')}
                                                    className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors rounded-b-lg"
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Images */}
                    {maintenance.images && maintenance.images.length > 0 && (
                        <div className="bg-light-card dark:bg-dark-card rounded-xl p-6 border border-light-border dark:border-dark-border">
                            <h3 className="text-lg font-bold text-light-text dark:text-dark-text mb-4 flex items-center gap-2">
                                <ImageIcon size={20} className="text-primary dark:text-golden" />
                                Attached Images
                            </h3>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                {maintenance.images.map((image, index) => (
                                    <img
                                        key={index}
                                        src={image}
                                        alt={`Attachment ${index + 1}`}
                                        className="w-full h-32 object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                    {/* Quick Info */}
                    <div className="bg-light-card dark:bg-dark-card rounded-xl p-6 border border-light-border dark:border-dark-border">
                        <h3 className="text-lg font-bold text-light-text dark:text-dark-text mb-4">Quick Info</h3>
                        <div className="space-y-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-light-bg dark:bg-dark-bg rounded-lg">
                                    <Building size={18} className="text-light-subtle dark:text-dark-subtle" />
                                </div>
                                <div>
                                    <p className="text-sm text-light-subtle dark:text-dark-subtle">Property</p>
                                    <p className="text-sm font-medium text-light-text dark:text-dark-text">{maintenance.property_name}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-light-bg dark:bg-dark-bg rounded-lg">
                                    <Calendar size={18} className="text-light-subtle dark:text-dark-subtle" />
                                </div>
                                <div>
                                    <p className="text-sm text-light-subtle dark:text-dark-subtle">Reported On</p>
                                    <p className="text-sm font-medium text-light-text dark:text-dark-text">
                                        {new Date(maintenance.created_at).toLocaleDateString('en-US', {
                                            year: 'numeric',
                                            month: 'short',
                                            day: 'numeric'
                                        })}
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-light-bg dark:bg-dark-bg rounded-lg">
                                    <Clock size={18} className="text-light-subtle dark:text-dark-subtle" />
                                </div>
                                <div>
                                    <p className="text-sm text-light-subtle dark:text-dark-subtle">Time</p>
                                    <p className="text-sm font-medium text-light-text dark:text-dark-text">
                                        {new Date(maintenance.created_at).toLocaleTimeString('en-US', {
                                            hour: '2-digit',
                                            minute: '2-digit'
                                        })}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Actions */}
                    {maintenance.status === 'Pending' && (
                        <div className="bg-light-card dark:bg-dark-card rounded-xl p-6 border border-light-border dark:border-dark-border">
                            <h3 className="text-lg font-bold text-light-text dark:text-dark-text mb-4">Actions</h3>
                            <div className="space-y-3">
                                <button
                                    onClick={() => handleUpdateStatus('In Progress')}
                                    disabled={actionLoading}
                                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                                >
                                    {actionLoading ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle size={18} />}
                                    Mark as In Progress
                                </button>
                                <button
                                    onClick={() => handleUpdateStatus('Cancelled')}
                                    disabled={actionLoading}
                                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                                >
                                    <XCircle size={18} />
                                    Cancel Request
                                </button>
                            </div>
                        </div>
                    )}

                    {maintenance.status === 'In Progress' && (
                        <div className="bg-light-card dark:bg-dark-card rounded-xl p-6 border border-light-border dark:border-dark-border">
                            <h3 className="text-lg font-bold text-light-text dark:text-dark-text mb-4">Actions</h3>
                            <button
                                onClick={() => handleUpdateStatus('Completed')}
                                disabled={actionLoading}
                                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                            >
                                {actionLoading ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle size={18} />}
                                Mark as Completed
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Backdrop for dropdown */}
            {showStatusDropdown && (
                <div
                    className="fixed inset-0 z-0"
                    onClick={() => setShowStatusDropdown(false)}
                />
            )}
        </div>
    );
};